import React, {useEffect, useRef, useState} from 'react';
import {$generateNodesFromDOM, $generateHtmlFromNodes} from '@lexical/html';
import DOMPurify from 'dompurify';
import {AIHelperDropdown} from '../../../AIHelper/AIHelperDropdown';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {mergeRegister} from '@lexical/utils';
import {
    $getRoot,
    $insertNodes,
    $getSelection,
    $isRangeSelection,
    COMMAND_PRIORITY_LOW,
    LexicalNode,
    UNDO_COMMAND
} from 'lexical';
import {HANDLE_AI_TEXT_SUGGESTION, INSERT_STRING_HTML, REPLACE_SELECTION_HTML} from './commands';
import AITexts from '../../../../Localization/AISearch/texts';
import {nodeType as styledTextNodeType} from '../../nodes/StyledText';
import {getAIContentEnhancement} from './actions';
import {extractSpecialTags, isNodeEmpty, restoreSpecialTags} from './utils';
import {monitorAction} from '../../../../services/datadog';
import {AIWritingAssistantMonitoring} from '../../../../constants/monitoring';
import {createDispatchCommand} from '../../utils';
import {noop} from '@priority-software/priority-style-react';

type Props = {
    portalId?: string;
    showSuccessToast?: (message: string) => void;
    language?: 'he' | 'en'; // Currently, only  English and Hebrew are supported;
    toggleAILoading?: (isLoading: boolean) => void;
    selectionMode?: boolean;
    disabled?: boolean;
};

export const EmailWritingAssistantPlugin = (props: Props) => {
    const {
        portalId,
        showSuccessToast,
        language,
        toggleAILoading,
        selectionMode = false,
        disabled
    } = props;
    const [editor] = useLexicalComposerContext();

    const isPendingAIRequest = useRef(false);
    const dispatchCommand = createDispatchCommand(editor);
    const imagesRef = useRef<string[]>([]);
    const hasContentRef = useRef(false);
    const [hasContent, setHasContent] = useState(false);
    const [hasSelection, setHasSelection] = useState(false);

    // AI content tracking for Ctrl+Z detection
    const aiContentHistoryRef = useRef<
        Array<{
            content: string;
            enhancementType: string;
            enhancementOption: string;
            timestamp: number;
        }>
    >([]);
    const previousContentRef = useRef<string>('');
    const lastAIActionRef = useRef<{
        enhancementType: string;
        enhancementOption: string;
        timestamp: number;
        contentBefore: string;
        contentAfter: string;
    } | null>(null);

    const handleSelect = async (enhancementType: string, enhancementOption: string) => {
        dispatchCommand(HANDLE_AI_TEXT_SUGGESTION, {
            enhancementOption,
            enhancementType,
            selectionMode: selectionMode
        });
    };

    // Check if editor has content
    useEffect(() => {
        const checkContent = () => {
            editor.read(() => {
                const text = $getRoot().getTextContent().trim();
                const newHasContent = text.length > 0;

                // Only update state if the value has changed
                if (hasContentRef.current !== newHasContent) {
                    hasContentRef.current = newHasContent;
                    setHasContent(newHasContent);
                }
            });
        };

        // Initial check
        checkContent();

        // Register for updates
        return editor.registerUpdateListener(checkContent);
    }, [editor]);

    // Track text selection when selectionMode mode is enabled
    useEffect(() => {
        if (!selectionMode) return noop;

        const updateSelection = () => {
            editor.read(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                    setHasSelection(selection.getTextContent().trim().length > 0);
                } else {
                    setHasSelection(false);
                }
            });
        };

        // Initial check
        updateSelection();

        // Register for selection updates
        return editor.registerUpdateListener(updateSelection);
    }, [editor, selectionMode]);

    useEffect(() => {
        return mergeRegister(
            // Monitor undo commands to detect AI content reversions
            editor.registerCommand(
                UNDO_COMMAND,
                () => {
                    // Check if the undo is reverting AI-generated content
                    setTimeout(() => {
                        editor.read(() => {
                            const currentContent = $generateHtmlFromNodes(editor);

                            // Check if we have a recent AI action and if the content was reverted
                            if (lastAIActionRef.current) {
                                const lastAIAction = lastAIActionRef.current;

                                // Check if current content matches the content before AI enhancement
                                // This indicates the AI enhancement was undone
                                const contentMatches =
                                    currentContent.trim() === lastAIAction.contentBefore.trim();
                                const timeDiff = Date.now() - lastAIAction.timestamp;
                                const wasRecentAIAction = timeDiff < 600000; // 10 minutes

                                if (contentMatches && wasRecentAIAction) {
                                    try {
                                        monitorAction(
                                            AIWritingAssistantMonitoring.AI_mailedi_ctrlz,
                                            {
                                                action: 'ai_content_reverted',
                                                revertedEnhancementType:
                                                    lastAIAction.enhancementType,
                                                revertedEnhancementOption:
                                                    lastAIAction.enhancementOption,
                                                revertedAt: Date.now(),
                                                originalTimestamp: lastAIAction.timestamp,
                                                component: 'EmailWritingAssistantPlugin'
                                            }
                                        );

                                        // Clear the last AI action after monitoring
                                        lastAIActionRef.current = null;
                                    } catch (error) {
                                        console.warn('AI monitoring failed:', error);
                                    }
                                }
                            }
                        });
                    }, 100); // Small delay to ensure undo has completed

                    return false; // Don't prevent the undo
                },
                COMMAND_PRIORITY_LOW
            ),

            editor.registerCommand(
                INSERT_STRING_HTML,
                ({html}) => {
                    editor.update(() => {
                        const parser = new DOMParser();
                        const dom = parser.parseFromString(html, 'text/html');

                        const nodes = $generateNodesFromDOM(editor, dom).filter(
                            (item: LexicalNode, index: number) => {
                                const hasFirstEmptyLine = index === 0 && isNodeEmpty(item);
                                const isBlockNode =
                                    item.getType() !== 'text' &&
                                    item.getType() !== styledTextNodeType;
                                return isBlockNode && !hasFirstEmptyLine;
                            }
                        );
                        $getRoot().clear();
                        $insertNodes(nodes);
                        $getRoot().selectEnd();
                    });
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),

            editor.registerCommand(
                REPLACE_SELECTION_HTML,
                ({html}) => {
                    editor.update(() => {
                        const selection = $getSelection();
                        if ($isRangeSelection(selection)) {
                            const parser = new DOMParser();
                            const dom = parser.parseFromString(html, 'text/html');
                            const nodes = $generateNodesFromDOM(editor, dom);
                            selection.insertNodes(nodes);
                        }
                    });
                    return false;
                },
                COMMAND_PRIORITY_LOW
            ),

            editor.registerCommand(
                HANDLE_AI_TEXT_SUGGESTION,
                ({enhancementOption, enhancementType, selectionMode}) => {
                    // Check if we can use `read` method after lexical version update
                    editor.update(() => {
                        (async () => {
                            let htmlBeforeSanitize = '';

                            // Determine content to process based on mode
                            if (selectionMode) {
                                const selection = $getSelection();
                                if ($isRangeSelection(selection) && !selection.isCollapsed()) {
                                    htmlBeforeSanitize = $generateHtmlFromNodes(editor, selection);
                                }
                            } else {
                                htmlBeforeSanitize = $generateHtmlFromNodes(editor);
                            }
                            // Store content before AI enhancement for Ctrl+Z detection
                            previousContentRef.current = htmlBeforeSanitize;

                            const mappedHtml = htmlBeforeSanitize.replace(
                                /style\s*=\s*(['"])(.*?)\1/gi,
                                'data-styles=$1$2$1'
                            );
                            const domParser = new DOMParser();
                            const docBefore = domParser.parseFromString(mappedHtml, 'text/html');

                            // Save image sources
                            docBefore.querySelectorAll('img').forEach((img, i) => {
                                imagesRef.current[i] = img.getAttribute('src') || '';
                            });

                            // Sanitize the HTML
                            const rawHtml = DOMPurify.sanitize(mappedHtml, {
                                ALLOWED_ATTR: [
                                    '_field_data',
                                    'data-styles',
                                    'width',
                                    'height',
                                    'alt'
                                ],
                                ADD_ATTR: ['_field_data']
                            });

                            const docCleaned = domParser.parseFromString(rawHtml, 'text/html');

                            // Remove image src for AI
                            docCleaned.querySelectorAll('img').forEach(img => {
                                img.removeAttribute('src');
                            });

                            const {cleanedHtml, tagMap} = extractSpecialTags(
                                docCleaned.body.innerHTML
                            );

                            if (!cleanedHtml || isPendingAIRequest.current) return;

                            await getAIContentEnhancement(
                                {
                                    content: cleanedHtml,
                                    enhancementType,
                                    enhancementOption,
                                    language
                                },
                                {
                                    onLoading: (flag: boolean) => {
                                        toggleAILoading?.(flag);
                                        isPendingAIRequest.current = flag;
                                    },
                                    handleResult: (htmlResponse: string) => {
                                        let html = htmlResponse.replace(
                                            /data-styles\s*=\s*(['"])(.*?)\1/gi,
                                            'style=$1$2$1'
                                        );

                                        if (tagMap.length > 0) {
                                            html = restoreSpecialTags(html, tagMap);
                                        }

                                        // Restore image sources
                                        if (imagesRef.current.length > 0) {
                                            const doc = new DOMParser().parseFromString(
                                                html,
                                                'text/html'
                                            );
                                            doc.querySelectorAll('img').forEach((img, i) => {
                                                const src = imagesRef.current[i];
                                                if (src) img.setAttribute('src', src);
                                            });
                                            html = doc.body.innerHTML;
                                        }

                                        // Track AI-generated content for Ctrl+Z detection
                                        const timestamp = Date.now();

                                        // Store in history array (for backward compatibility)
                                        aiContentHistoryRef.current.push({
                                            content: html,
                                            enhancementType,
                                            enhancementOption,
                                            timestamp
                                        });

                                        // Keep only last 10 AI enhancements to prevent memory issues
                                        if (aiContentHistoryRef.current.length > 10) {
                                            aiContentHistoryRef.current =
                                                aiContentHistoryRef.current.slice(-10);
                                        }

                                        // Store the last AI action for precise Ctrl+Z detection
                                        lastAIActionRef.current = {
                                            enhancementType,
                                            enhancementOption,
                                            timestamp,
                                            contentBefore: previousContentRef.current,
                                            contentAfter: html
                                        };

                                        // Use appropriate command based on mode
                                        if (selectionMode) {
                                            dispatchCommand(REPLACE_SELECTION_HTML, {html});
                                        } else {
                                            dispatchCommand(INSERT_STRING_HTML, {html});
                                        }
                                        imagesRef.current = [];
                                    },
                                    onSuccess: () => {
                                        showSuccessToast?.(
                                            `${AITexts.ai_email_enhancement_success}\n${AITexts.ai_email_enhancement_success_additional}`
                                        );
                                    },
                                    onErrorClose: () => {
                                        requestAnimationFrame(() => {
                                            if (selectionMode) {
                                                // TODO: For selection mode, try to restore selection
                                                editor.focus();
                                            } else {
                                                editor.update(() => {
                                                    $getRoot().selectEnd();
                                                });
                                            }
                                        });
                                    }
                                }
                            );
                        })();
                    });
                    return false;
                },
                COMMAND_PRIORITY_LOW
            )
        );
    }, [editor, toggleAILoading]);

    const isDropdownDisabled = disabled || (selectionMode ? !hasSelection : !hasContent);

    return (
        <AIHelperDropdown
            onSelect={handleSelect}
            portalId={portalId}
            disabled={isDropdownDisabled}
        />
    );
};
